import React, { useState, useEffect } from "react";
import { MySQLAdminData } from "../types";

interface InterestTabProps {
  data: MySQLAdminData;
}

type PeriodType = "day" | "week" | "biweek" | "month" | "3months" | "6months";

interface PeriodInfo {
  label: string;
  startDate: string;
  endDate: string;
}

export const InterestTab: React.FC<InterestTabProps> = ({ data }) => {
  const [periodType, setPeriodType] = useState<PeriodType>("month");
  const [currentPeriodOffset, setCurrentPeriodOffset] = useState(0);
  const [periodInfo, setPeriodInfo] = useState<PeriodInfo | null>(null);
  const [topStocksData, setTopStocksData] = useState(data.topInterestStocks);
  const [isLoading, setIsLoading] = useState(false);

  // 기간 계산 함수
  const calculatePeriod = (type: PeriodType, offset: number): PeriodInfo => {
    const today = new Date();
    let startDate: Date;
    let endDate: Date;
    let label: string;

    switch (type) {
      case "day": {
        // 1일 단위: 특정 날짜 하루
        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() + offset);
        startDate = new Date(targetDate);
        endDate = new Date(targetDate);
        label = formatDate(startDate);
        break;
      }
      case "week": {
        // 1주 단위: 월요일~일요일
        const currentDay = today.getDay(); // 0: 일요일, 1: 월요일, ...
        const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
        const thisMonday = new Date(today);
        thisMonday.setDate(today.getDate() + mondayOffset);

        const targetMonday = new Date(thisMonday);
        targetMonday.setDate(thisMonday.getDate() + offset * 7);

        startDate = new Date(targetMonday);
        endDate = new Date(targetMonday);
        endDate.setDate(targetMonday.getDate() + 6);

        // 현재 주이고 오늘이 일요일 전이면 오늘까지만
        if (offset === 0 && endDate > today) {
          endDate = new Date(today);
        }

        label = `${formatDate(startDate)} ~ ${formatDate(endDate)}`;
        break;
      }
      case "biweek": {
        // 2주 단위: 1~14일, 15~말일
        const currentDay = today.getDate();
        const isCurrentFirstHalf = currentDay <= 14;

        // 전체 2주 기간의 수를 계산 (현재 기간을 0으로)
        // offset이 -1이면 한 기간 이전, -2면 두 기간 이전
        let periodsBack = -offset;

        // 현재 기간부터 거슬러 올라가기
        let tempMonth = today.getMonth();
        let tempYear = today.getFullYear();
        let tempIsFirstHalf = isCurrentFirstHalf;

        while (periodsBack > 0) {
          if (tempIsFirstHalf) {
            // 상반기에서 이전으로 가면 -> 이전 달 하반기
            tempMonth--;
            if (tempMonth < 0) {
              tempMonth = 11;
              tempYear--;
            }
            tempIsFirstHalf = false;
          } else {
            // 하반기에서 이전으로 가면 -> 같은 달 상반기
            tempIsFirstHalf = true;
          }
          periodsBack--;
        }

        // 목표 기간의 시작일과 종료일 설정
        if (tempIsFirstHalf) {
          startDate = new Date(tempYear, tempMonth, 1);
          endDate = new Date(tempYear, tempMonth, 14);
        } else {
          startDate = new Date(tempYear, tempMonth, 15);
          endDate = new Date(tempYear, tempMonth + 1, 0); // 해당 월의 마지막 날
        }

        // offset=0이고 현재 기간이면 오늘까지만
        if (offset === 0 && endDate > today) {
          endDate = new Date(today);
        }

        label = `${formatDate(startDate)} ~ ${formatDate(endDate)}`;
        break;
      }
      case "month": {
        // 1개월 단위: 해당 월의 1일~말일
        const targetMonth = new Date(
          today.getFullYear(),
          today.getMonth() + offset,
          1
        );
        startDate = new Date(
          targetMonth.getFullYear(),
          targetMonth.getMonth(),
          1
        );
        endDate = new Date(
          targetMonth.getFullYear(),
          targetMonth.getMonth() + 1,
          0
        );

        // 현재 월이면 오늘까지만
        if (offset === 0) {
          endDate = new Date(today);
        }

        label = `${targetMonth.getFullYear()}년 ${
          targetMonth.getMonth() + 1
        }월`;
        break;
      }
      case "3months": {
        // 3개월 단위
        const targetMonth = new Date(
          today.getFullYear(),
          today.getMonth() + offset * 3,
          1
        );
        startDate = new Date(
          targetMonth.getFullYear(),
          targetMonth.getMonth(),
          1
        );
        endDate = new Date(
          targetMonth.getFullYear(),
          targetMonth.getMonth() + 3,
          0
        );

        // 현재 기간이면 오늘까지만
        if (offset === 0) {
          endDate = new Date(today);
        }

        const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
        label = `${targetMonth.getFullYear()}년 ${
          targetMonth.getMonth() + 1
        }월 ~ ${endMonth.getFullYear()}년 ${endMonth.getMonth() + 1}월`;
        break;
      }
      case "6months": {
        // 6개월 단위
        const targetMonth = new Date(
          today.getFullYear(),
          today.getMonth() + offset * 6,
          1
        );
        startDate = new Date(
          targetMonth.getFullYear(),
          targetMonth.getMonth(),
          1
        );
        endDate = new Date(
          targetMonth.getFullYear(),
          targetMonth.getMonth() + 6,
          0
        );

        // 현재 기간이면 오늘까지만
        if (offset === 0) {
          endDate = new Date(today);
        }

        const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
        label = `${targetMonth.getFullYear()}년 ${
          targetMonth.getMonth() + 1
        }월 ~ ${endMonth.getFullYear()}년 ${endMonth.getMonth() + 1}월`;
        break;
      }
    }

    return {
      label,
      startDate: formatDateForAPI(startDate),
      endDate: formatDateForAPI(endDate),
    };
  };

  // 날짜 포맷 함수
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDateForAPI = (date: Date): string => {
    return formatDate(date);
  };

  // 데이터 페칭 함수
  const fetchTopStocks = async (start: string, end: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `/api/admin/mysql?type=topInterestStocksByPeriod&startDate=${start}&endDate=${end}`
      );
      const newData = await response.json();
      setTopStocksData(newData);
    } catch (error) {
      console.error("Failed to fetch top stocks:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // 기간 변경 시 데이터 업데이트
  useEffect(() => {
    const period = calculatePeriod(periodType, currentPeriodOffset);
    setPeriodInfo(period);
    fetchTopStocks(period.startDate, period.endDate);
  }, [periodType, currentPeriodOffset]);

  // 기간 타입 변경 핸들러
  const handlePeriodTypeChange = (newType: PeriodType) => {
    setPeriodType(newType);
    setCurrentPeriodOffset(0); // 기간 타입 변경 시 오프셋 초기화
  };

  // 이전/다음 기간 이동 핸들러
  const handlePrevPeriod = () => {
    setCurrentPeriodOffset(currentPeriodOffset - 1);
  };

  const handleNextPeriod = () => {
    // 현재 기간(offset=0)보다 미래로는 이동 불가
    if (currentPeriodOffset < 0) {
      setCurrentPeriodOffset(currentPeriodOffset + 1);
    }
  };

  const canGoNext = currentPeriodOffset < 0;

  return (
    <div className="space-y-8">
      {/* 관심종목 현황 KPI */}
      <section className="glass-card">
        <h2 className="text-3xl font-bold tracking-tight text-white/90 glow-text mb-6">
          관심종목 현황
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            title="총 관심종목 사용자"
            value={`${data.interestStats.totalInterestUsers.toLocaleString()}명`}
            subtitle={`전체 대비 ${data.interestStats.totalInterestUsersRate}%`}
            accent="blue"
          />
          <KPICard
            title="평균 관심종목 수"
            value={`${parseFloat(data.interestStats.avgInterestPerUser).toFixed(
              1
            )}개`}
            subtitle="사용자당"
            accent="cyan"
          />
          <KPICard
            title="총 관심종목 등록"
            value={data.interestStats.totalInterestCount.toLocaleString()}
            subtitle="전체 등록 건수"
            accent="violet"
          />
          <KPICard
            title="최근 30일 활성"
            value={`${data.interestStats.newUserInterest30d.toLocaleString()}명`}
            subtitle={`신규 가입자 대비 ${data.interestStats.interestUsersRate30d}%`}
            accent="pink"
          />
        </div>
      </section>

      {/* 기간별 관심종목 사용자 */}
      <section className="glass-card">
        <h2 className="text-2xl font-semibold mb-6 text-white/90 glow-text">
          기간별 관심종목 등록 사용자
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <PeriodStat
            period="최근 1일"
            count={data.interestStats.newUserInterest1d}
            rate={data.interestStats.interestUsersRate1d}
            color="from-emerald-500 to-teal-500"
          />
          <PeriodStat
            period="최근 7일"
            count={data.interestStats.newUserInterest7d}
            rate={data.interestStats.interestUsersRate7d}
            color="from-blue-500 to-cyan-500"
          />
          <PeriodStat
            period="최근 30일"
            count={data.interestStats.newUserInterest30d}
            rate={data.interestStats.interestUsersRate30d}
            color="from-violet-500 to-purple-500"
          />
        </div>
      </section>

      {/* 인기 관심종목 TOP 10 */}
      <section className="glass-card">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-white/90 glow-text mb-4">
            인기 관심종목 TOP 10
          </h2>

          {/* 기간 선택 및 네비게이션 */}
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* 기간 타입 선택 */}
            <div className="flex gap-2 flex-wrap">
              <PeriodButton
                label="1일"
                active={periodType === "day"}
                onClick={() => handlePeriodTypeChange("day")}
              />
              <PeriodButton
                label="1주"
                active={periodType === "week"}
                onClick={() => handlePeriodTypeChange("week")}
              />
              <PeriodButton
                label="2주"
                active={periodType === "biweek"}
                onClick={() => handlePeriodTypeChange("biweek")}
              />
              <PeriodButton
                label="1개월"
                active={periodType === "month"}
                onClick={() => handlePeriodTypeChange("month")}
              />
              <PeriodButton
                label="3개월"
                active={periodType === "3months"}
                onClick={() => handlePeriodTypeChange("3months")}
              />
              <PeriodButton
                label="6개월"
                active={periodType === "6months"}
                onClick={() => handlePeriodTypeChange("6months")}
              />
            </div>

            {/* 기간 네비게이션 */}
            <div className="flex items-center gap-3 md:ml-auto">
              <button
                onClick={handlePrevPeriod}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all"
                title="이전 기간"
              >
                <svg
                  className="w-5 h-5 text-white/70"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              <div className="px-4 py-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-400/30 rounded-lg min-w-[200px] text-center">
                <span className="text-white/90 font-medium text-sm">
                  {periodInfo?.label || ""}
                </span>
              </div>

              <button
                onClick={handleNextPeriod}
                disabled={!canGoNext}
                className={`p-2 rounded-lg border transition-all ${
                  canGoNext
                    ? "bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/20"
                    : "bg-white/2 border-white/5 cursor-not-allowed opacity-40"
                }`}
                title="다음 기간"
              >
                <svg
                  className="w-5 h-5 text-white/70"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <TopStocksTable data={topStocksData} isLoading={isLoading} />
      </section>
    </div>
  );
};

function KPICard({
  title,
  value,
  subtitle,
  accent = "blue",
}: {
  title: string;
  value: string;
  subtitle: string;
  accent?: "blue" | "cyan" | "violet" | "pink" | "emerald";
}) {
  const accentColors = {
    blue: "from-blue-400/20 to-cyan-400/20 border-blue-400/30 shadow-blue-400/20",
    cyan: "from-cyan-400/20 to-teal-400/20 border-cyan-400/30 shadow-cyan-400/20",
    violet:
      "from-violet-400/20 to-purple-400/20 border-violet-400/30 shadow-violet-400/20",
    pink: "from-pink-400/20 to-rose-400/20 border-pink-400/30 shadow-pink-400/20",
    emerald:
      "from-emerald-400/20 to-green-400/20 border-emerald-400/30 shadow-emerald-400/20",
  };

  return (
    <div className={`glass-kpi ${accentColors[accent]} border border-white/10`}>
      <div className="text-white/70 text-sm tracking-wide font-medium mb-1">
        {title}
      </div>
      <div className="text-2xl font-bold text-white/95 mb-1">{value}</div>
      <div className="text-white/60 text-xs">{subtitle}</div>
    </div>
  );
}

function PeriodStat({
  period,
  count,
  rate,
  color,
}: {
  period: string;
  count: number;
  rate: string;
  color: string;
}) {
  return (
    <div className="glass-sub-card">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-2 h-12 rounded-full bg-gradient-to-b ${color}`} />
        <div>
          <div className="text-white/80 font-medium text-sm">{period}</div>
          <div className="text-2xl font-bold text-white/95">
            {count.toLocaleString()}
          </div>
        </div>
      </div>
      <div className="text-white/60 text-sm">
        해당 기간 신규 가입자 대비{" "}
        <span className="text-cyan-300 font-medium">{rate}%</span>
      </div>
    </div>
  );
}

function PeriodButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg border transition-all text-sm font-medium ${
        active
          ? "bg-gradient-to-r from-blue-500/30 to-cyan-500/30 border-blue-400/50 text-white shadow-lg shadow-blue-500/20"
          : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:border-white/20"
      }`}
    >
      {label}
    </button>
  );
}

function TopStocksTable({
  data,
  isLoading,
}: {
  data: Array<{
    ticker: string;
    stockName: string;
    userCount: number;
    totalCount: number;
  }>;
  isLoading?: boolean;
}) {
  if (isLoading) {
    return (
      <div className="text-center py-12 h-[80vh]">
        {/* <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
        <div className="text-white/60 text-lg glow-text mt-4"></div> */}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 h-[80vh]">
        <div className="text-white/60 text-lg glow-text">데이터 없음</div>
      </div>
    );
  }

  return (
    <div className="glass-table-container scrollable-table h-[80vh]">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-white/10 backdrop-blur-md z-10">
          <tr>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20 w-16">
              순위
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              티커
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              종목명
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              관심 등록 사용자
            </th>
            {/* <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              총 등록 횟수
            </th> */}
          </tr>
        </thead>
        <tbody>
          {data.map((stock, index) => (
            <tr
              key={stock.ticker}
              className={index % 2 === 0 ? "bg-white/2" : ""}
            >
              <td className="px-4 py-3 border-b border-white/10">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    index === 0
                      ? "bg-gradient-to-r from-yellow-400 to-yellow-600 text-yellow-900"
                      : index === 1
                      ? "bg-gradient-to-r from-gray-300 to-gray-500 text-gray-900"
                      : index === 2
                      ? "bg-gradient-to-r from-amber-600 to-amber-800 text-amber-100"
                      : "bg-white/10 text-white/70"
                  }`}
                >
                  {index + 1}
                </div>
              </td>
              <td className="px-4 py-3 text-cyan-300 font-mono text-base border-b border-white/10">
                {stock.ticker}
              </td>
              <td className="px-4 py-3 text-white/80 border-b border-white/10 text-base font-medium">
                {stock.stockName || "-"}
              </td>
              <td className="px-4 py-3 text-violet-300 font-bold border-b border-white/10 text-base">
                {stock.userCount.toLocaleString()}명
              </td>
              {/* <td className="px-4 py-3 text-pink-300 font-bold border-b border-white/10 text-base">
                {stock.totalCount.toLocaleString()}회
              </td> */}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
